import React from "react";
import * as Mui from "@mui/material";
import { withStyles } from "tss-react/mui";
import type { WithStyles } from "type/tss";
import MuiAlert from "@mui/material/Alert";
import { DashboardLayout } from "component/layout/dashboard";
import { FullPageCard } from "component/shared/full-page-card";
import { SettingsStickyFooter } from "component/shared/settings-sticky-footer";
import { UnsavedChangesPrompt } from "component/shared/unsaved-changes-prompt";
import { RoundRobinTab } from "./round-robin-tab"
import { GeographicTab } from "./geographic-tab"
import { FeaturedTab } from "./featured-tab"
import { FirstTouchTab } from "./first-touch-tab"
import { ConfirmationModal } from "./confirmation-modal";
import { connect } from "react-redux";
import { getRoundRobinRoutes } from "redux/selector";
import { getGeographicRoutes } from "redux/selector";
import { RootState } from "redux/store";
import { bindActionCreators, Dispatch } from "redux";
import * as Router from "react-router-dom";
import * as Icons from "react-feather";
import { FeatherIcon } from "component/shared/feather-icon";
import { getUserUnsafe } from "redux/selector";
import { saveUser } from "redux/slice/authentication";
import { UserPartial } from "model/user";
import { urls } from "routes/urls";
import * as env from "shared/env";
import { styles } from "./style";

interface Props
	extends ReturnType<typeof mapStateToProps>,
	ReturnType<typeof mapDispatchToProps>,
	WithStyles<typeof styles>,
	Router.RouteComponentProps{}

interface RoutingPanel {
	label: string;
	content: React.ReactElement;
	badge?: string;
}

const mapStateToProps = (state: RootState) => {
	const roundRobinRoutes = getRoundRobinRoutes(state);
	const geographicRoutes = getGeographicRoutes(state);
	const user = getUserUnsafe(state);
	return {
		roundRobinRoutes,
		geographicRoutes,
		user,
		authLoading: state.authentication.loading,
		authError: state.authentication.error,
	}
}

interface State {
	addAgentIsOpen: boolean;
	tab: number;
	saving: boolean;
	firstTouchRouting: boolean;
	listingAgentRouting: boolean;
	geographicRouting: boolean;
	roundRobinRouting: boolean;
	confirmModal: "firstTouch" | "featured" | null;
}

const mapDispatchToProps = (dispatch: Dispatch) =>
	bindActionCreators({ saveUser }, dispatch);


class Component extends React.Component<Props, State> {

	public constructor(props: Props) {
		super(props);
		const { user } = props;
		this.state = {
			addAgentIsOpen: false,
			tab: 0,
			saving: false,
			firstTouchRouting: user?.firstTouchRouting || false,
			listingAgentRouting: user?.listingAgentRouting || false,
			geographicRouting: user?.geographicRouting || false,
			roundRobinRouting: user?.roundRobinRouting || false,
			confirmModal: null,
		};
	}

	private isDirty(): boolean {
		const { user } = this.props;
		const { firstTouchRouting, listingAgentRouting, geographicRouting, roundRobinRouting } = this.state;
		return (
			firstTouchRouting !== (user?.firstTouchRouting || false) ||
			listingAgentRouting !== (user?.listingAgentRouting || false) ||
			geographicRouting !== (user?.geographicRouting || false) ||
			roundRobinRouting !== (user?.roundRobinRouting || false)
		);
	}

	private save() {
		const { saveUser } = this.props;
		const { firstTouchRouting, listingAgentRouting, geographicRouting, roundRobinRouting } = this.state;
		const user: UserPartial = {
			firstTouchRouting,
			listingAgentRouting,
			geographicRouting,
			roundRobinRouting,
		};
		saveUser({ user });
		this.setState({ saving: true });
	}

	private cancel() {
		const { user } = this.props;
		this.setState({
			firstTouchRouting: user?.firstTouchRouting || false,
			listingAgentRouting: user?.listingAgentRouting || false,
			geographicRouting: user?.geographicRouting || false,
			roundRobinRouting: user?.roundRobinRouting || false,
		});
	}

	// Legacy tab order (flag off). Retained until the Lead Routing refresh (MAX-3649) ships.
	private buildTabs(): RoutingPanel[] {
		const { roundRobinRoutes, geographicRoutes, user } = this.props;
		const tabs: RoutingPanel[] = [];

		if (user?.permissions.roundRobinRouting) {
			tabs.push({ label: "Round Robin", content: <RoundRobinTab roundRobinRoutes={roundRobinRoutes} /> });
		}
		if (user?.permissions.geographicRouting) {
			tabs.push({ label: "Geographic", content: <GeographicTab geographicRoutes={geographicRoutes} /> });
		}
		tabs.push({ label: "Featured", content: <FeaturedTab /> });
		if (!user?.kestrelVersion.off && user?.permissions.firstTouchRouting) {
			tabs.push({ label: "First Touch Attribution", content: <FirstTouchTab /> });
		}

		return tabs;
	}

	// Refresh (flag on): single page, sections in routing-rule priority order (highest first):
	// First Touch Attribution > Featured Listings > Geographic > Round Robin.
	// The primary toggles are controlled here so the sticky save bar can batch them.
	private buildSections(): RoutingPanel[] {
		const { roundRobinRoutes, geographicRoutes, user } = this.props;
		const { firstTouchRouting, listingAgentRouting, geographicRouting, roundRobinRouting } = this.state;
		const sections: RoutingPanel[] = [];

		// 1. First Touch Attribution — highest priority (overrides all other rules)
		if (!user?.kestrelVersion.off && user?.permissions.firstTouchRouting) {
			sections.push({
				label: "First Touch Attribution",
				badge: "Highest Priority",
				// Enabling overrides all other rules — confirm first. Disabling is immediate.
				content: <FirstTouchTab checked={firstTouchRouting} onToggle={(v) => v ? this.setState({ confirmModal: "firstTouch" }) : this.setState({ firstTouchRouting: false })} />,
			});
		}
		// 2. Featured Listings — always available
		sections.push({
			label: "Featured Listings",
			// Enabling overrides Geographic/Round Robin — confirm first. Disabling is immediate.
			content: <FeaturedTab checked={listingAgentRouting} onToggle={(v) => v ? this.setState({ confirmModal: "featured" }) : this.setState({ listingAgentRouting: false })} />,
		});
		// 3. Geographic
		if (user?.permissions.geographicRouting) {
			sections.push({
				label: "Geographic",
				content: <GeographicTab geographicRoutes={geographicRoutes} checked={geographicRouting} onToggle={(v) => this.setState({ geographicRouting: v })} />,
			});
		}
		// 4. Round Robin — lowest priority
		if (user?.permissions.roundRobinRouting) {
			sections.push({
				label: "Round Robin",
				content: <RoundRobinTab roundRobinRoutes={roundRobinRoutes} checked={roundRobinRouting} onToggle={(v) => this.setState({ roundRobinRouting: v })} />,
			});
		}

		return sections;
	}

	private renderRefresh() {
		const { classes = {}, authLoading, authError } = this.props;
		const { saving, confirmModal } = this.state;
		const sections = this.buildSections();
		const dirty = this.isDirty();
		return (
			<>
				<ConfirmationModal
					open={confirmModal === "firstTouch"}
					title="Enable First Touch Attribution?"
					confirmLabel="Enable First Touch Attribution"
					body={
						<>
							<Mui.Typography variant="body1" style={{ marginBottom: 12 }}>
								This will override all other active routing rules. Leads will be assigned to the first agent page they visit, regardless of your Geographic or Featured routing settings.
							</Mui.Typography>
							<Mui.Typography variant="body1">
								If a website visitor doesn't visit an agent page, they can select an agent from the capture form (if enabled), otherwise the lead will fall back to your other routing rules.
							</Mui.Typography>
						</>
					}
					onCancel={() => this.setState({ confirmModal: null })}
					onConfirm={() => this.setState({ firstTouchRouting: true, confirmModal: null })}
				/>
				<ConfirmationModal
					open={confirmModal === "featured"}
					title="Enable Featured Listing Routing?"
					confirmLabel="Enable Featured Listing Routing"
					body={
						<>
							<Mui.Typography variant="body1" style={{ marginBottom: 12 }}>
								This will override your Geographic and Round Robin routing rules for any leads on featured listings.
							</Mui.Typography>
							<Mui.Typography variant="body1">
								Note: First Touch Attribution will still take precedence if enabled.
							</Mui.Typography>
						</>
					}
					onCancel={() => this.setState({ confirmModal: null })}
					onConfirm={() => this.setState({ listingAgentRouting: true, confirmModal: null })}
				/>
				<Mui.Typography variant="body2" color="textSecondary" style={{ marginBottom: 24 }}>
					Control how new leads are assigned to agents on your team. You can enable one or more routing methods below. When multiple methods are active, leads are assigned based on the following priority order: First Touch Attribution → Featured Listings → Geographic → Round Robin.
				</Mui.Typography>
				{sections.map((section) => (
					<Mui.Box mb={2} key={section.label}>
						<FullPageCard>
							<div className={classes.sectionHeaderRow}>
								<Mui.Typography className={classes.sectionHeader}>
									{section.label}
								</Mui.Typography>
								{section.badge && <Mui.Chip label={section.badge} className={classes.priorityBadge} />}
							</div>
							{section.content}
						</FullPageCard>
					</Mui.Box>
				))}
				<SettingsStickyFooter
					dirty={dirty}
					saving={saving}
					authError={authError}
					onSave={() => this.save()}
					onCancel={() => this.cancel()}
				/>
				<UnsavedChangesPrompt isDirty={() => this.isDirty()} history={this.props.history} />
				<Mui.Snackbar
					open={saving && !authLoading && !authError}
					autoHideDuration={6000}
					onClose={() => this.setState({ saving: false })}
				>
					<MuiAlert severity="info">
						<Mui.Typography>Settings saved</Mui.Typography>
					</MuiAlert>
				</Mui.Snackbar>
			</>
		);
	}

	private renderTabs() {
		const { tab } = this.state;
		const availableTabs = this.buildTabs();
		return (
			<Mui.Grid container direction="column" spacing={2} lg={8} xl={6}>
				<Mui.Grid item>
					<Mui.Tabs
						indicatorColor="secondary"
						textColor="secondary"
						value={tab}
						onChange={(event, newTab) => {
							if (newTab >= 0 && newTab < availableTabs.length) {
								this.setState({ tab: newTab });
							}
						}}
					>
						{availableTabs.map((tabConfig, index) => (
							<Mui.Tab key={index} label={tabConfig.label} />
						))}
					</Mui.Tabs>
				</Mui.Grid>
				<Mui.Grid item>
					{availableTabs[tab]?.content}
				</Mui.Grid>
			</Mui.Grid>
		);
	}

	public render() {
		const title = "Lead Routing";
		const { user } = this.props;

		return (
			<DashboardLayout
				permitted={user && user.permissions.leads}
				title={'Lead Routing'}
				header={
					<Mui.Typography variant="h1">
						<FeatherIcon>
							<Icons.User />
						</FeatherIcon>
						{title}
					</Mui.Typography>
				}
			>
				{user?.permissions.showTeamFeatures ? (
					env.refreshDesignsDev ? this.renderRefresh() : this.renderTabs()
				) : (
					<Mui.Grid container direction="column" spacing={3} alignItems="center" style={{ marginTop: "64px" }}>
						<Mui.Grid item>
							<Mui.Typography variant="h4" align="center">
								No team members yet!
							</Mui.Typography>
						</Mui.Grid>
						<Mui.Grid item>
							<Mui.Typography variant="body1" align="center" color="textSecondary">
								Lead routing lets you automatically distribute new leads to your team.
								<br />
								Add at least one other team member to use lead routing.
							</Mui.Typography>
						</Mui.Grid>
						<Mui.Grid item>
							<Mui.Button
								variant="contained"
								color="primary"
								component={Router.Link}
								to={urls.settingsAgents}
							>
								Add Team Member
							</Mui.Button>
						</Mui.Grid>
					</Mui.Grid>
				)}
			</DashboardLayout>
		);
	}

}

export const SettingsLeadRoutingPage = withStyles(connect(mapStateToProps, mapDispatchToProps)(Component), styles);
